import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  FetchInventoryTypesResponse,
  FetchInventoryTypeByIdResponse,
  CreateInventoryTypeResponse,
  UpdateInventoryTypeResponse,
  DeleteInventoryTypeResponse,
  ActivateInventoryTypeResponse,
  SuspendInventoryTypeResponse,
  InventoryTypeState,
} from './types'
import * as actions from './service'

const initialState: InventoryTypeState = {
  inventoryTypes: [],
  inventoryTypeForEdit: null,
  listLoading: false,
  actionLoading: false,
  error: null,
  total: 0,
  totalPages: 0,
}

const inventoryTypeSlice = createSlice({
  name: 'inventoryType',
  initialState,
  reducers: {
    setInventoryTypeForEdit: (state, action: PayloadAction<any>) => {
      state.inventoryTypeForEdit = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Inventory Types
    builder
      .addCase(actions.fetchInventoryTypes.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(actions.fetchInventoryTypes.fulfilled, (state, action: PayloadAction<FetchInventoryTypesResponse>) => {
        state.listLoading = false
        state.inventoryTypes = action.payload.inventoryTypes
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      })
      .addCase(actions.fetchInventoryTypes.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.payload
      })

    // Fetch All Inventory Types
    builder
      .addCase(actions.fetchAllInventoryTypes.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(actions.fetchAllInventoryTypes.fulfilled, (state, action: PayloadAction<FetchInventoryTypesResponse>) => {
        state.listLoading = false
        state.inventoryTypes = action.payload.inventoryTypes
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      })
      .addCase(actions.fetchAllInventoryTypes.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.payload
      })

    // Fetch Inventory Type By ID
    builder
      .addCase(actions.fetchInventoryTypeById.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.fetchInventoryTypeById.fulfilled, (state, action: PayloadAction<FetchInventoryTypeByIdResponse>) => {
        state.actionLoading = false
        state.inventoryTypeForEdit = action.payload.inventoryType
      })
      .addCase(actions.fetchInventoryTypeById.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Create Inventory Type
    builder
      .addCase(actions.createInventoryType.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.createInventoryType.fulfilled, (state, action: PayloadAction<CreateInventoryTypeResponse>) => {
        state.actionLoading = false
        state.inventoryTypes.unshift(action.payload.inventoryType)
      })
      .addCase(actions.createInventoryType.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Update Inventory Type
    builder
      .addCase(actions.updateInventoryType.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.updateInventoryType.fulfilled, (state, action: PayloadAction<UpdateInventoryTypeResponse>) => {
        state.actionLoading = false
        const index = state.inventoryTypes.findIndex(item => item._id === action.payload.inventoryType._id)
        if (index !== -1) {
          state.inventoryTypes[index] = action.payload.inventoryType
        }
      })
      .addCase(actions.updateInventoryType.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Delete Inventory Type
    builder
      .addCase(actions.deleteInventoryType.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.deleteInventoryType.fulfilled, (state, action) => {
        state.actionLoading = false
        state.inventoryTypes = state.inventoryTypes.filter(item => item._id !== action.meta.arg.id)
      })
      .addCase(actions.deleteInventoryType.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Activate Inventory Type
    builder
      .addCase(actions.activateInventoryType.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.activateInventoryType.fulfilled, (state, action: PayloadAction<ActivateInventoryTypeResponse>) => {
        state.actionLoading = false
        const index = state.inventoryTypes.findIndex(item => item._id === action.payload.inventoryType._id)
        if (index !== -1) {
          state.inventoryTypes[index] = action.payload.inventoryType
        }
      })
      .addCase(actions.activateInventoryType.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Suspend Inventory Type
    builder
      .addCase(actions.suspendInventoryType.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(actions.suspendInventoryType.fulfilled, (state, action: PayloadAction<SuspendInventoryTypeResponse>) => {
        state.actionLoading = false
        const index = state.inventoryTypes.findIndex(item => item._id === action.payload.inventoryType._id)
        if (index !== -1) {
          state.inventoryTypes[index] = action.payload.inventoryType
        }
      })
      .addCase(actions.suspendInventoryType.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })
  },
})

export const {setInventoryTypeForEdit, clearError} = inventoryTypeSlice.actions
export default inventoryTypeSlice.reducer
